export type ClinicSaveStatus =
  "idle" | "dirty" | "saving" | "confirmed" | "failed" | "conflict";

export type ClinicSaveState<T> = {
  status: ClinicSaveStatus;
  draft: T;
  confirmed: T;
  failedValue: T | null;
  hasUnresolvedDraft: boolean;
  serverConflict: boolean;
};

type ClinicSaveCoordinatorOptions<T> = {
  initialValue: T;
  equals: (left: T, right: T) => boolean;
  save: (value: T) => Promise<T>;
  onChange: (state: ClinicSaveState<T>) => void;
};

export type ClinicSaveCoordinator<T> = {
  getState: () => ClinicSaveState<T>;
  updateDraft: (value: T) => void;
  saveDraft: (value: T) => void;
  reconcileServerValue: (value: T) => ClinicSaveState<T>;
  retry: () => void;
  cancel: () => void;
  destroy: () => void;
};

/**
 * Coordinates compact edits whose optimistic display must not be presented
 * as confirmed until the server responds. The coordinator deliberately
 * keeps the attempted value after a rejected request so retry sends the same
 * value unless the patient has edited it again.
 */
export function createClinicSaveCoordinator<T>({
  initialValue,
  equals,
  save,
  onChange,
}: ClinicSaveCoordinatorOptions<T>): ClinicSaveCoordinator<T> {
  let confirmed = initialValue;
  let draft = initialValue;
  let latestServerValue = initialValue;
  let failedValue: T | null = null;
  let inFlightValue: T | null = null;
  let queuedValue: T | null = null;
  let serverConflict = false;
  let status: ClinicSaveStatus = "idle";
  let destroyed = false;

  const getState = (): ClinicSaveState<T> => ({
    status,
    draft,
    confirmed,
    failedValue,
    hasUnresolvedDraft:
      serverConflict ||
      inFlightValue !== null ||
      queuedValue !== null ||
      failedValue !== null ||
      !equals(draft, confirmed),
    serverConflict,
  });

  const emit = () => {
    if (!destroyed) onChange(getState());
  };

  const setIdle = () => {
    failedValue = null;
    queuedValue = null;
    serverConflict = false;
    status = "idle";
  };

  const send = (value: T): void => {
    if (destroyed) return;

    if (equals(value, confirmed)) {
      draft = value;
      setIdle();
      emit();
      return;
    }

    inFlightValue = value;
    failedValue = null;
    status = "saving";
    emit();

    save(value).then(
      (serverValue) => {
        if (destroyed) return;

        inFlightValue = null;
        confirmed = serverValue;
        latestServerValue = serverValue;
        serverConflict = false;

        const nextValue = queuedValue;
        queuedValue = null;

        if (nextValue !== null && !equals(nextValue, serverValue)) {
          send(nextValue);
          return;
        }

        if (nextValue !== null) draft = nextValue;
        if (equals(draft, serverValue)) {
          draft = serverValue;
          status = "confirmed";
          failedValue = null;
        } else {
          status = "dirty";
        }
        emit();
      },
      () => {
        if (destroyed) return;

        inFlightValue = null;
        const nextValue = queuedValue;
        queuedValue = null;

        if (nextValue !== null) {
          send(nextValue);
          return;
        }

        failedValue = value;
        status = "failed";
        emit();
      },
    );
  };

  const updateDraft = (value: T) => {
    draft = value;
    if (equals(value, confirmed) && inFlightValue === null) {
      failedValue = null;
      queuedValue = null;
      serverConflict = false;
      status = "idle";
    } else if (inFlightValue === null) {
      status = "dirty";
    }
    emit();
  };

  const saveDraft = (value: T) => {
    draft = value;

    if (inFlightValue !== null) {
      if (!equals(value, inFlightValue)) queuedValue = value;
      status = "saving";
      emit();
      return;
    }

    if (equals(value, confirmed)) {
      setIdle();
      emit();
      return;
    }

    send(value);
  };

  const reconcileServerValue = (value: T): ClinicSaveState<T> => {
    latestServerValue = value;

    const hasLocalWork =
      serverConflict ||
      inFlightValue !== null ||
      queuedValue !== null ||
      failedValue !== null ||
      !equals(draft, confirmed);

    if (!hasLocalWork) {
      const keepConfirmedStatus =
        status === "confirmed" && equals(value, confirmed);
      confirmed = value;
      draft = value;
      status = keepConfirmedStatus ? "confirmed" : "idle";
      serverConflict = false;
      emit();
      return getState();
    }

    if (equals(value, confirmed)) return getState();

    if (equals(value, draft)) {
      confirmed = value;
      if (inFlightValue === null && queuedValue === null) {
        failedValue = null;
        serverConflict = false;
        status = "confirmed";
      }
      emit();
      return getState();
    }

    serverConflict = true;
    status = "conflict";
    emit();
    return getState();
  };

  const retry = () => {
    if (inFlightValue !== null) return;
    const retryValue =
      failedValue !== null && equals(draft, failedValue) ? failedValue : draft;
    saveDraft(retryValue);
  };

  const cancel = () => {
    if (inFlightValue !== null) return;
    const value = serverConflict ? latestServerValue : confirmed;
    confirmed = value;
    draft = value;
    latestServerValue = value;
    setIdle();
    emit();
  };

  return {
    getState,
    updateDraft,
    saveDraft,
    reconcileServerValue,
    retry,
    cancel,
    destroy: () => {
      destroyed = true;
    },
  };
}
